import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarNav,
  CalendarPrev,
  CalendarNext,
  CalendarToday,
} from '@akoom-tech/mobiscroll-react';
//
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { format, isValid } from 'date-fns';
//
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { getActiveProviders } from '@/features/users/shared/api';
import Iconify from '@/components/Iconify';
import {
  SERVICE_LABEL_LEGEND,
  type SchedulerBookingTypeFilter,
  type SchedulerIssueFilter,
  type SchedulerSessionStatusFilter,
} from '../../utils';
//
type MbscCalendarHeaderProps = {
  handleCleanerFilter?: (cleanerId: number) => void;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  highlightProviderLabel?: string | null;
  onClearHighlightProvider?: () => void;
  activeStatusFilters?: SchedulerSessionStatusFilter[];
  onToggleStatusFilter?: (status: SchedulerSessionStatusFilter) => void;
  activeBookingTypeFilters?: SchedulerBookingTypeFilter[];
  onToggleBookingTypeFilter?: (bookingType: SchedulerBookingTypeFilter) => void;
  activeIssueFilters?: SchedulerIssueFilter[];
  onToggleIssueFilter?: (issue: SchedulerIssueFilter) => void;
};

type CleanerOption = {
  id: number;
  label: string;
  group: 'All' | 'Active';
  searchValue: string;
};

const STATUS_LEGEND_ITEMS: {
  label: string;
  color: string;
  value: SchedulerSessionStatusFilter;
}[] = [
  { label: 'In progress', color: '#2F6FED', value: 'in_progress' },
  { label: 'On the way', color: '#F59E0B', value: 'on_the_way' },
  { label: 'Completed', color: '#22C55E', value: 'completed' },
  { label: 'Upcoming', color: '#64748B', value: 'upcoming' },
];

const SERVICE_LEGEND_ORDER: SchedulerBookingTypeFilter[] = [
  'PW',
  'PF',
  'PO',
  'HO',
];

const isSchedulerBookingTypeFilter = (
  code: string
): code is SchedulerBookingTypeFilter =>
  SERVICE_LEGEND_ORDER.includes(code as SchedulerBookingTypeFilter);

const ISSUE_LEGEND_ITEMS: {
  label: string;
  color: string;
  value: SchedulerIssueFilter;
}[] = [
  { label: 'Arrival risk', color: '#EF4444', value: 'arrival_risk' },
  { label: 'Late start', color: '#64748B', value: 'late_start' },
];

export default function MbscCalendarHeader({
  handleCleanerFilter = () => {},
  selectedDate,
  onSelectDate,
  highlightProviderLabel,
  onClearHighlightProvider,
  activeStatusFilters = [],
  onToggleStatusFilter,
  activeBookingTypeFilters = [],
  onToggleBookingTypeFilter,
  activeIssueFilters = [],
  onToggleIssueFilter,
}: MbscCalendarHeaderProps) {
  const { errorMsg } = useSnackbarMsg();

  const cleanersQuery = useQuery({
    queryKey: ['providers', 'active'],
    queryFn: () => getActiveProviders(),
    onError: (error: unknown) => errorMsg(error, 'Error fetching cleaners'),
  });

  const cleaners = cleanersQuery.data;

  const allCleanersOption = useMemo<CleanerOption>(
    () => ({
      id: 0,
      label: 'All',
      group: 'All',
      searchValue: 'all cleaners',
    }),
    []
  );

  const activeCleanerOptions = useMemo<CleanerOption[]>(() => {
    if (!cleaners) return [];

    return cleaners
      .filter((cleaner) => !cleaner.deleted)
      .map((cleaner) => {
        const fullName = `${cleaner.first_name} ${cleaner.last_name}`.trim();
        const label = fullName || cleaner.email || `Provider #${cleaner.id}`;

        return {
          id: cleaner.id,
          label,
          group: 'Active',
          searchValue: [fullName, cleaner.email ?? '']
            .filter(Boolean)
            .join(' ')
            .trim(),
        };
      });
  }, [cleaners]);

  const sortedCleanerOptions = useMemo(
    () => [allCleanersOption, ...activeCleanerOptions],
    [allCleanersOption, activeCleanerOptions]
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions<CleanerOption>({
        stringify: (option) => option.searchValue,
      }),
    []
  );

  const [selectedCleanerId, setSelectedCleanerId] = useState<number>(0);

  const selectedCleanerOption = useMemo(
    () =>
      sortedCleanerOptions.find((option) => option.id === selectedCleanerId) ??
      allCleanersOption,
    [allCleanersOption, selectedCleanerId, sortedCleanerOptions]
  );

  const formattedSelectedDate = useMemo(
    () => format(selectedDate, 'dd MMM yyyy'),
    [selectedDate]
  );

  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const isDatePopoverOpen = Boolean(dateAnchorEl);
  const activeStatusFilterSet = useMemo(
    () => new Set(activeStatusFilters),
    [activeStatusFilters]
  );
  const hasActiveStatusFilters = activeStatusFilters.length > 0;
  const activeBookingTypeFilterSet = useMemo(
    () => new Set(activeBookingTypeFilters),
    [activeBookingTypeFilters]
  );
  const hasActiveBookingTypeFilters = activeBookingTypeFilters.length > 0;
  const activeIssueFilterSet = useMemo(
    () => new Set(activeIssueFilters),
    [activeIssueFilters]
  );
  const hasActiveIssueFilters = activeIssueFilters.length > 0;

  const handleOpenDatePopover = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleCloseDatePopover = () => {
    setDateAnchorEl(null);
  };

  const handleDateSelection = (value: Date | null) => {
    if (value && isValid(value)) {
      onSelectDate(value);
      handleCloseDatePopover();
    }
  };

  const serviceLegendItems = useMemo(() => {
    const seen = new Set<string>();
    return SERVICE_LABEL_LEGEND.filter(({ code }) => {
      if (!isSchedulerBookingTypeFilter(code)) {
        return false;
      }
      if (seen.has(code)) {
        return false;
      }
      seen.add(code);
      return true;
    }).sort(
      (a, b) =>
        SERVICE_LEGEND_ORDER.indexOf(a.code as SchedulerBookingTypeFilter) -
        SERVICE_LEGEND_ORDER.indexOf(b.code as SchedulerBookingTypeFilter)
    );
  }, []);

  const serviceLegendTooltip = useMemo(
    () => (
      <Box>
        {serviceLegendItems.map(({ code, description }) => (
          <Typography
            key={code}
            variant="caption"
            component="div"
            sx={{ lineHeight: 1.4 }}
          >
            {`${code} - ${description}`}
          </Typography>
        ))}
      </Box>
    ),
    [serviceLegendItems]
  );

  return (
    <>
      <Stack
        spacing={0.75}
        sx={{
          width: '100%',
          pb: 0.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <CalendarNav className="md-header-filter-nav" />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: '100%', pl: 2 }}
          >
            {cleaners ? (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Iconify icon="eva:calendar-outline" width={16} />}
                  onClick={handleOpenDatePopover}
                  sx={{ textTransform: 'none' }}
                >
                  {formattedSelectedDate}
                </Button>
                <Autocomplete
                  value={selectedCleanerOption}
                  options={sortedCleanerOptions}
                  disableClearable
                  groupBy={(option) => option.group}
                  filterOptions={filterOptions}
                  sx={{ width: 220 }}
                  size="small"
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_event, option) => {
                    const cleanerId = option?.id ?? 0;
                    setSelectedCleanerId(cleanerId);
                    handleCleanerFilter(cleanerId);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select cleaner" />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <ul style={{ padding: 0, margin: 0 }}>
                        {params.children}
                      </ul>
                    </li>
                  )}
                  getOptionLabel={(option) => option.label}
                />
                <Tooltip title={serviceLegendTooltip} placement="bottom" arrow>
                  <IconButton size="small">
                    <Iconify icon="eva:info-outline" width={18} />
                  </IconButton>
                </Tooltip>
                {highlightProviderLabel && (
                  <Chip
                    size="small"
                    color="info"
                    label={`Highlighting ${highlightProviderLabel}`}
                    onDelete={onClearHighlightProvider}
                  />
                )}
              </Stack>
            ) : (
              <Skeleton variant="rectangular" width={260} height={40} />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarPrev className="md-header-filter-prev" />
            <CalendarToday className="md-header-filter-today" />
            <CalendarNext className="md-header-filter-next" />
          </Stack>
        </Stack>

        <Stack spacing={0.25} sx={{ px: 1, color: 'text.secondary' }}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{ minHeight: 24 }}
          >
            {STATUS_LEGEND_ITEMS.map((item) => {
              const isSelected = activeStatusFilterSet.has(item.value);

              return (
                <Tooltip key={item.value} title={`Filter ${item.label}`} arrow>
                  <ButtonBase
                    component="button"
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggleStatusFilter?.(item.value)}
                    sx={{
                      borderRadius: 0.75,
                      px: 0.6,
                      py: 0.35,
                      opacity: hasActiveStatusFilters && !isSelected ? 0.5 : 1,
                      border: '1px solid',
                      borderColor: isSelected ? item.color : 'transparent',
                      backgroundColor: isSelected
                        ? `${item.color}14`
                        : 'transparent',
                      transition: (theme) =>
                        theme.transitions.create(
                          ['background-color', 'border-color', 'opacity'],
                          {
                            duration: theme.transitions.duration.shortest,
                          }
                        ),
                      '&:hover': {
                        opacity: 1,
                        borderColor: item.color,
                        backgroundColor: `${item.color}14`,
                      },
                    }}
                  >
                    <LegendItem
                      label={item.label}
                      labelColor={
                        isSelected ? 'text.primary' : 'text.secondary'
                      }
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: 0.5,
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                    </LegendItem>
                  </ButtonBase>
                </Tooltip>
              );
            })}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{ minHeight: 24 }}
          >
            {serviceLegendItems.map(({ code, description }) => {
              const bookingType = code as SchedulerBookingTypeFilter;
              const isSelected = activeBookingTypeFilterSet.has(bookingType);
              const accentColor = bookingType === 'HO' ? '#EF4444' : '#2F6FED';

              return (
                <Tooltip key={code} title={`Filter ${description}`} arrow>
                  <ButtonBase
                    component="button"
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggleBookingTypeFilter?.(bookingType)}
                    sx={{
                      borderRadius: 0.75,
                      px: 0.6,
                      py: 0.35,
                      opacity:
                        hasActiveBookingTypeFilters && !isSelected ? 0.5 : 1,
                      border: '1px solid',
                      borderColor: isSelected ? accentColor : 'transparent',
                      backgroundColor: isSelected
                        ? `${accentColor}14`
                        : 'transparent',
                      transition: (theme) =>
                        theme.transitions.create(
                          ['background-color', 'border-color', 'opacity'],
                          {
                            duration: theme.transitions.duration.shortest,
                          }
                        ),
                      '&:hover': {
                        opacity: 1,
                        borderColor: accentColor,
                        backgroundColor: `${accentColor}14`,
                      },
                    }}
                  >
                    <LegendItem
                      label={description}
                      labelColor={
                        isSelected ? 'text.primary' : 'text.secondary'
                      }
                    >
                      <ServiceLegendBadge code={code} />
                    </LegendItem>
                  </ButtonBase>
                </Tooltip>
              );
            })}

            {ISSUE_LEGEND_ITEMS.map((item) => {
              const isSelected = activeIssueFilterSet.has(item.value);

              return (
                <Tooltip key={item.value} title={`Filter ${item.label}`} arrow>
                  <ButtonBase
                    component="button"
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggleIssueFilter?.(item.value)}
                    sx={{
                      borderRadius: 0.75,
                      px: 0.6,
                      py: 0.35,
                      opacity: hasActiveIssueFilters && !isSelected ? 0.5 : 1,
                      border: '1px solid',
                      borderColor: isSelected ? item.color : 'transparent',
                      backgroundColor: isSelected
                        ? `${item.color}14`
                        : 'transparent',
                      transition: (theme) =>
                        theme.transitions.create(
                          ['background-color', 'border-color', 'opacity'],
                          {
                            duration: theme.transitions.duration.shortest,
                          }
                        ),
                      '&:hover': {
                        opacity: 1,
                        borderColor: item.color,
                        backgroundColor: `${item.color}14`,
                      },
                    }}
                  >
                    <LegendItem
                      label={item.label}
                      labelColor={
                        isSelected ? 'text.primary' : 'text.secondary'
                      }
                    >
                      {item.value === 'arrival_risk' ? (
                        <Iconify icon="eva:alert-triangle-outline" width={15} />
                      ) : (
                        <Box
                          sx={{
                            width: 18,
                            height: 14,
                            borderRadius: 0.5,
                            border: '1px dashed',
                            borderColor: isSelected
                              ? 'text.primary'
                              : 'text.secondary',
                            backgroundImage: `repeating-linear-gradient(45deg, rgba(99, 115, 129, 0.18) 0px, rgba(99, 115, 129, 0.18) 4px, transparent 4px, transparent 8px)`,
                          }}
                        />
                      )}
                    </LegendItem>
                  </ButtonBase>
                </Tooltip>
              );
            })}
          </Stack>
        </Stack>
      </Stack>

      <Popover
        open={isDatePopoverOpen}
        anchorEl={dateAnchorEl}
        onClose={handleCloseDatePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CalendarPicker
              date={selectedDate}
              onChange={handleDateSelection}
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
}

function LegendItem({
  children,
  label,
  labelColor = 'text.secondary',
}: {
  children: React.ReactNode;
  label: string;
  labelColor?: 'text.primary' | 'text.secondary';
}) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" minWidth={0}>
      {children}
      <Typography
        variant="caption"
        color={labelColor}
        sx={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function ServiceLegendBadge({ code }: { code: string }) {
  const isHoliday = code === 'HO';

  return (
    <Box
      sx={{
        height: 20,
        minWidth: 30,
        px: 0.6,
        borderRadius: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'common.white',
        backgroundColor: isHoliday ? 'error.main' : 'info.main',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {code}
    </Box>
  );
}
